import { Fragment, useState } from "react";
import {
  ActivityIcon,
  ArrowRightIcon,
  CalendarClockIcon,
  ChevronRightIcon,
  ScanSearchIcon,
} from "lucide-react";

import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { Chip, Tag, WhenCell } from "@/components/tools/assistant/shared";
import {
  type ChipDef,
  MESSAGE_KIND,
  ORIGIN,
  RUN_RESULT,
  type Tone,
} from "@/components/tools/assistant/vocab";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useGetAssistantOverview,
  useGetAssistantRuns,
} from "@/hooks/queries/useGetAssistantAdmin";
import { cn } from "@/lib/utils";
import { AssistantRunLog } from "@/types";

const KINDS: { key: "email_scan" | "schedule_tick"; def: ChipDef }[] = [
  {
    key: "email_scan",
    def: {
      label: "Email scan · hourly",
      tone: "slate",
      Icon: ScanSearchIcon,
      hint: "Reads every mailbox, finds unanswered customer emails, nudges Staffers.",
    },
  },
  {
    key: "schedule_tick",
    def: {
      label: "Schedule tick · 15 min",
      tone: "teal",
      Icon: CalendarClockIcon,
      hint: "Hands out reminders, follow-ups and held nudges that are due.",
    },
  },
];

// The scan's funnel, left to right.
const FUNNEL: { key: keyof NonNullable<AssistantRunLog["stats"]>; label: string; tone: Tone }[] = [
  { key: "threads", label: "threads", tone: "slate" },
  { key: "conversations", label: "conversations", tone: "slate" },
  { key: "pastPrefilter", label: "past prefilter", tone: "sky" },
  { key: "classified", label: "classified", tone: "blue" },
  { key: "flagged", label: "flagged", tone: "amber" },
];

export const AssistantRunsTab = ({ days }: { days: number }) => {
  const [kind, setKind] = useState<"email_scan" | "schedule_tick">("email_scan");
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data: runs, status } = useGetAssistantRuns({ kind });
  // Only for staffer names in the per-staffer breakdown.
  const { data: overview } = useGetAssistantOverview({ days });
  const names = new Map(
    (overview?.threads ?? []).map((t) => [t.staffer.id, t.staffer.name]),
  );

  return (
    <Column className="min-h-0 flex-1 gap-4">
      <div className="flex gap-2">
        {KINDS.map((k) => (
          <button
            className={cn(
              "rounded-full ring-offset-2 transition",
              kind === k.key ? "ring-2 ring-slate-400" : "opacity-70 hover:opacity-100",
            )}
            key={k.key}
            onClick={() => {
              setKind(k.key);
              setExpanded(null);
            }}
            type="button"
          >
            <Chip def={k.def} />
          </button>
        ))}
      </div>
      {status === "pending" ? (
        <LoadingIndicator variant="medium" />
      ) : status === "error" ? (
        <ErrorText errorMessage="Error loading runs." />
      ) : runs.length === 0 ? (
        <EmptyState
          description="Each scan and tick records itself here when it finishes."
          Icon={ActivityIcon}
          title="No runs logged yet"
        />
      ) : (
        <Column className="overflow-auto pb-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28px]" />
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Took</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>What it did</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => {
                const perStaffer = run.stats?.perStaffer ?? [];
                const isOpen = expanded === run.id;
                const deferred = perStaffer.reduce((n, p) => n + (p.deferred ?? 0), 0);
                const sent = perStaffer.reduce((n, p) => n + p.sent, 0);
                return (
                  <Fragment key={run.id}>
                    <TableRow
                      className={cn(
                        perStaffer.length && "cursor-pointer",
                        run.error && "bg-red-50/50",
                      )}
                      onClick={() => {
                        if (perStaffer.length) setExpanded(isOpen ? null : run.id);
                      }}
                    >
                      <TableCell>
                        {perStaffer.length > 0 && (
                          <ChevronRightIcon
                            className={cn(
                              "size-4 text-slate-400 transition-transform",
                              isOpen && "rotate-90",
                            )}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <WhenCell iso={run.startedAt} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {run.durationMs === null
                          ? "—"
                          : `${(run.durationMs / 1000).toFixed(1)}s`}
                      </TableCell>
                      <TableCell>
                        <Chip def={run.error ? RUN_RESULT.failed : RUN_RESULT.ok} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1.5">
                          {run.kind === "schedule_tick" ? (
                            <Chip
                              count={run.stats?.claimed ?? 0}
                              def={{ ...KINDS[1].def, label: "schedules handed out" }}
                            />
                          ) : (
                            <>
                              <div className="flex flex-wrap items-center gap-1">
                                {FUNNEL.map((f, i) => (
                                  <span className="inline-flex items-center gap-1" key={f.key}>
                                    {i > 0 && (
                                      <ArrowRightIcon className="size-3 text-slate-300" />
                                    )}
                                    <Tag tone={f.tone}>
                                      <span className="mr-1 font-semibold tabular-nums">
                                        {Number(run.stats?.[f.key] ?? 0).toLocaleString()}
                                      </span>
                                      {f.label}
                                    </Tag>
                                  </span>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                <Tag tone={sent ? "green" : "slate"}>
                                  <span className="mr-1 font-semibold">{sent}</span>
                                  sent to Staffers
                                </Tag>
                                {deferred > 0 && (
                                  <Chip
                                    count={deferred}
                                    def={ORIGIN.held_for_chat}
                                  />
                                )}
                              </div>
                            </>
                          )}
                          {run.error && (
                            <p className="rounded-md bg-red-100 px-2 py-1 text-xs text-red-700">
                              {run.error}
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableCell colSpan={5}>
                          <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2">
                            {perStaffer.map((p) => (
                              <div
                                className="flex flex-col gap-1.5 rounded-lg border bg-white p-3"
                                key={p.stafferUserId}
                              >
                                <span className="text-sm font-medium text-slate-900">
                                  {names.get(p.stafferUserId) ??
                                    `User ${p.stafferUserId.toString()}`}
                                </span>
                                <div className="flex flex-wrap gap-1">
                                  <Tag>
                                    <b className="mr-1">{p.signals}</b>open emails
                                  </Tag>
                                  <Tag tone="blue">
                                    <b className="mr-1">{p.planned}</b>due to send
                                  </Tag>
                                  <Tag tone={p.sent ? "green" : "slate"}>
                                    <b className="mr-1">{p.sent}</b>sent
                                  </Tag>
                                  {p.held > 0 && (
                                    <Chip count={p.held} def={ORIGIN.follow_up} />
                                  )}
                                  {(p.deferred ?? 0) > 0 && (
                                    <Chip count={p.deferred} def={ORIGIN.held_for_chat} />
                                  )}
                                </div>
                                {(p.quietDue !== undefined || p.newsDue !== undefined) && (
                                  <div className="flex flex-wrap gap-1">
                                    {p.quietDue !== undefined && (
                                      <Chip
                                        def={MESSAGE_KIND.quiet_customer}
                                        detail={`${(p.quietSent ?? 0).toString()}/${p.quietDue.toString()} sent`}
                                      />
                                    )}
                                    {p.newsDue !== undefined && (
                                      <Chip
                                        def={MESSAGE_KIND.candidate_news}
                                        detail={`${(p.newsSent ?? 0).toString()}/${p.newsDue.toString()} sent`}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Column>
      )}
    </Column>
  );
};
